<template>
  <mailbox-settings-layout :name="savedName">
    <div class="mailbox-settings-general-settings">
      <el-card>
        <template #header>
          <h3>General Settings</h3>
        </template>
        <entity-settings-loading v-if="isLoading" />
        <template v-else>
          <el-form :disabled="isSubmitting">
            <el-form-item prop="name">
              <h5>Mailbox name</h5>
              <el-input v-model="name" maxlength="50" />
            </el-form-item>
            <!-- Tags list -->
            <el-form-item prop="tags" class="tags-list">
              <h5>Manage tags</h5>
              <p>
                Select tags which you’d like to delete from this mailbox. This action will also remove them
                from all conversations and custom filters.
              </p>
              <dd-tag
                v-for="tag in tags"
                :key="tag.id"
                :disabled="isSubmitting"
                :text="tag.name"
                type="input"
                @close="addToTagIDsToDelete(tag.id)"
              />
              <p v-if="tags.length === 0" class="tags-list__empty-message">
                There are no tags in this mailbox.
              </p>
            </el-form-item>
          </el-form>

          <dd-button
            :is-disabled="isSubmitting"
            :is-loading="isUpdating"
            :color="ColorAssistant.MailboxColor"
            size="large"
            class="float-right"
            @click="updateMailbox()"
          >Save Changes</dd-button>

          <div class="clearfix" />
        </template>
      </el-card>

      <!-- Archive and remove card -->
      <el-card class="remove-card">
        <template #header>
          <h3>Remove Mailbox</h3>
        </template>
        <entity-settings-loading v-if="isLoading" />
        <template v-else>
          <h4>Archive</h4>
          <p>You can reactivate archived mailboxes at any time from your dashboard.</p>
          <p><br></p>
          <dd-button
            :is-disabled="isSubmitting"
            :is-loading="isArchiving"
            class="btn--archive"
            type="flat"
            size="medium"
            @click="handleArchive"
          >
            <i class="ddi ddi-lock" />&nbsp;Archive
          </dd-button>

          <div class="dd-divider" />

          <h4>Delete</h4>
          <p>Deleted mailboxes are removed permanently.</p>
          <p><br></p>
          <dd-button
            :is-disabled="isSubmitting"
            :is-loading="isDeleting"
            class="btn--delete"
            type="flat"
            size="medium"
            @click="handleDelete"
          >
            <i class="ddi ddi-trash" />&nbsp;Delete
          </dd-button>
        </template>
      </el-card>
    </div>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'
import * as routerNames from '@/services/router/router.names'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IItemForSelection } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesInputGeneralSettings } from '@/api/types/request.types'

export default defineComponent({
  name: 'MailboxSettingsGeneralSettings',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isUpdating: false,
      isArchiving: false,
      isDeleting: false,

      tagIDsToDelete: [] as number[],

      savedName: '',
      name: '',
      tags: [] as IItemForSelection[],
    }
  },

  head(): any {
    return {
      title: `General Settings - ${this.savedName}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    isSubmitting(): boolean {
      return this.isUpdating || this.isArchiving || this.isDeleting
    },
  },

  async created() {
    this.isLoading = true
    await this.getGeneralSettingsInfo()
    this.isLoading = false
  },

  methods: {
    addToTagIDsToDelete(id: number) {
      this.tags = this.tags.filter(t => t.id !== id)
      if (!this.tagIDsToDelete.includes(id)) {
        this.tagIDsToDelete.push(id)
      }
    },
    async getGeneralSettingsInfo() {
      try {
        const response = await MailboxesAPI.get_GeneralSettings(this.id)
        this.name = response.name
        this.savedName = this.name
        this.tags = response.listTags
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async updateMailbox() {
      try {
        this.isUpdating = true
        const data = {
          name: this.name,
          listTagIDsToDelete: this.tagIDsToDelete,
        } as IMailboxesInputGeneralSettings
        const response = await MailboxesAPI.post_GeneralSettings(this.id, data)
        this.savedName = this.name
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error)
        this.name = this.savedName
      } finally {
        this.isUpdating = false
      }
    },
    async handleArchive(): Promise<void> {
      try {
        await this.$confirm(
          `<strong>Forwarded emails to this mailbox will no longer be processed while this is archived.</strong>
          <br>Are you sure you want to archive this mailbox?`,
          {
            dangerouslyUseHTMLString: true,
            center: true,
            confirmButtonText: 'Yes, Archive',
            confirmButtonClass: 'el-button--primary',
            cancelButtonText: 'Cancel',
            cancelButtonClass: 'el-button--text',
            type: 'warning',
            title: `Archive this Mailbox`,
          },
        )
        this.isArchiving = true
        await MailboxesAPI.post_Archive(this.id)
        this.$router.push({ name: routerNames.DASHBOARD })
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isArchiving = false
      }
    },
    async handleDelete(): Promise<void> {
      try {
        await this.$confirm(
          `<strong>Forwarded emails to this mailbox will no longer be processed. This action is permanent.</strong>
          <br>Are you sure you want to delete this mailbox?`,
          {
            dangerouslyUseHTMLString: true,
            center: true,
            confirmButtonText: 'Yes, Delete',
            confirmButtonClass: 'el-button--strawberry',
            cancelButtonText: 'Cancel',
            cancelButtonClass: 'el-button--text',
            type: 'warning',
            title: `Delete this Mailbox`,
          },
        )
        this.isDeleting = true
        await MailboxesAPI.post_Delete(this.id)
        this.$router.push({ name: routerNames.DASHBOARD })
        this.isDeleting = false
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isDeleting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-general-settings {
  .dd-tag {
    float: left;
    margin-top: 13px;
    margin-right: 6px;
  }

  .tags-list {
    &__empty-message {
      margin-top: 24px;
      color: var(--color-text-third);
    }
  }

  .remove-card {
    margin-top: 24px;
    .dd-divider {
      margin-top: 24px;
    }
  }

  .ddi-trash {
    color: $strawberry;
  }
}
</style>
